// Admin ingredient-DB types. Mirror the backend IngredientRecord /
// IngredientSummary, with Firestore Timestamps serialized to ISO strings.

import { PantryCategory } from './pantry';

export type IngredientStatus = 'resolved' | 'unresolved' | 'not_ingredient';

// How a record's category/isStaple were assigned.
export type ClassificationSource = 'curated' | 'ai' | 'admin';

// Human labels for the pantry categories, in display order. Used by the admin
// browser filter and the detail view.
export const INGREDIENT_CATEGORY_LABELS: Record<PantryCategory, string> = {
  produce: 'Produce',
  meat: 'Meat & protein',
  dairy: 'Dairy',
  pantry_dry_goods: 'Pantry / dry goods',
  spices_seasonings: 'Spices & seasonings',
  frozen: 'Frozen',
  bakery: 'Bakery',
  beverages: 'Beverages',
  other: 'Other',
};

export const INGREDIENT_CATEGORY_ORDER = Object.keys(
  INGREDIENT_CATEGORY_LABELS
) as PantryCategory[];

// Lightweight row from GET /admin/ingredients (loaded once, searched in memory).
export type IngredientSummary = {
  name: string;
  // Human display name (most-common raw phrasing); falls back to `name`.
  displayName?: string;
  defaultUnit?: string;
  status: IngredientStatus;
  degraded: boolean;
  pricePerUnitMid?: number;
  imageUrl?: string;
  // Classification — absent on still-unclassified records.
  category?: PantryCategory;
  isStaple?: boolean;
};

export type IngredientNutrition = {
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
  fiber: number;
  sodium: number;
  saturatedFat: number;
  transFat: number;
  sugar: number;
  cholesterol: number;
};

export type IngredientProductTier = {
  krogerProductId: string;
  upc: string;
  brand?: string;
  description: string;
  imageUrl?: string;
  productPageURI?: string;
  price: number;
  size: string;
  soldBy: 'UNIT' | 'WEIGHT';
  pricePerUnit?: number;
  nutrition?: IngredientNutrition;
  servingSize?: { quantity: number; unit: string; label?: string };
  servingsPerContainer?: number;
  nutritionSource?: 'kroger' | 'ai_estimate';
  nutritionalRating?: number;
  confidence?: number;
  reasoning?: string;
};

export type IngredientUnitConversion = {
  fromUnit: string;
  toUnit: string;
  factor: number;
  preparation?: string;
  notes?: string;
};

// Full record from GET /admin/ingredients/:name.
export type IngredientRecord = {
  id: string;
  normalizedName: string;
  // Display names ranked most-common-first; [0] is primary, rest are aliases.
  displayNames?: string[];
  defaultUnit?: string;
  unitConversions?: IngredientUnitConversion[];
  productTiers: {
    value?: IngredientProductTier;
    mid?: IngredientProductTier;
    premium?: IngredientProductTier;
  };
  notAnIngredient?: boolean;
  notAnIngredientReason?: string;
  // Classification — absent on still-unclassified records.
  category?: PantryCategory;
  isStaple?: boolean;
  classificationSource?: ClassificationSource;
  locationId: string;
  // ISO strings (serialized from Firestore Timestamps), or null.
  createdAt: string | null;
  lastUpdated: string | null;
  lastKrogerSync: string | null;
  lastSuccessfulKrogerSync: string | null;
  lastRefreshDegradedAt: string | null;
};

// A recipe that references an ingredient, from GET /admin/ingredients/:name/recipes.
export type RecipeUsingIngredient = {
  type: 'user' | 'catalog';
  recipeId: string;
  // Owner's user ID — present for user recipes (needed to build the link).
  userId?: string;
  name: string;
  origin: string;
  servings: number;
  imageUrl?: string;
  isCurated?: boolean;
};

export type IngredientRecipes = {
  user: RecipeUsingIngredientGroup;
  catalog: RecipeUsingIngredientGroup;
};

// A capped slice of recipes plus the true match count for that collection.
export type RecipeUsingIngredientGroup = {
  recipes: RecipeUsingIngredient[];
  total: number;
};

export const KROGER_BASE_URL = 'https://www.kroger.com';
